import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import type { Baermann } from '@/features/analisis/baermann/types';
import type { Coprocultivo } from '@/features/analisis/coprocultivo/types';
import type { Ectoparasitos } from '@/features/analisis/ectoparasitos/types';
import type { SedimentacionFh } from '@/features/analisis/sedimentacion-fh/types';
import { formatNumeroAnalisis } from '@/features/analisis/utils/formatNumeroAnalisis';
import { ResultadoBinario, TipoAnalisis } from '@/types/enums';
import { capitalize, formatDateAR, formatInteger } from '@/utils/formatters';

import type { GrupoAnalisis, GrupoHpg, GrupoIndividual } from '../grupoAnalisis';

const TIPO_COMP_SINGULAR: Record<string, string> = {
  [TipoAnalisis.OPG]: 'OPG',
  [TipoAnalisis.COPROCULTIVO]: 'Coprocultivo',
  [TipoAnalisis.BAERMANN]: 'Baermann',
  [TipoAnalisis.SEDIMENTACION_FH]: 'Sed. Fh',
};

const TIPO_COMP_PLURAL: Record<string, string> = {
  [TipoAnalisis.OPG]: 'OPGs',
  [TipoAnalisis.COPROCULTIVO]: 'coprocultivos',
  [TipoAnalisis.BAERMANN]: 'baermanns',
  [TipoAnalisis.SEDIMENTACION_FH]: 'Sed. Fh',
};

interface HpgRowInfo {
  titulo: string;
  subtitulo1: string;
  subtitulo2: string;
}

function getHpgInfo(grupo: GrupoHpg, numeroProtocolo?: number): HpgRowInfo {
  const { hpg, complementarios } = grupo;
  const fechaStr = formatDateAR(hpg.fecha_creacion);
  const tipoComp = complementarios[0]?.tipo_analisis ?? null;
  const n = complementarios.length;

  let tituloAnalisis = 'HPG';
  if (n === 1 && tipoComp) {
    tituloAnalisis = `HPG + ${TIPO_COMP_SINGULAR[tipoComp] ?? tipoComp}`;
  } else if (n > 1 && tipoComp) {
    tituloAnalisis = `HPG + ${TIPO_COMP_SINGULAR[tipoComp] ?? tipoComp} (×${n})`;
  }

  const numFmt = (() => {
    if (numeroProtocolo == null) return null;
    const nums = [hpg.numero_analisis, ...complementarios.map((c) => c.numero_analisis)].filter(
      (x): x is number => x != null,
    );
    if (nums.length === 0) return null;
    return nums.map((n) => `${numeroProtocolo}/${n}`).join(' - ');
  })();
  const titulo = numFmt ? `${numFmt} — ${tituloAnalisis}` : tituloAnalisis;

  const mCount = hpg.muestras_hpg.length;
  let subtitulo1 = `${mCount} muestra${mCount !== 1 ? 's' : ''} HPG`;
  if (n > 0 && tipoComp) {
    subtitulo1 += ` · ${n} ${TIPO_COMP_PLURAL[tipoComp] ?? tipoComp}`;
  }

  const valores = hpg.muestras_hpg
    .filter((m) => m.valor !== null)
    .map((m) => m.valor as number);
  const avg =
    valores.length > 0
      ? Math.round(valores.reduce((s, v) => s + v, 0) / valores.length)
      : null;
  const subtitulo2 =
    `${avg !== null ? `Promedio: ${formatInteger(avg)} hpg · ` : ''}Cargado el ${fechaStr}`;

  return { titulo, subtitulo1, subtitulo2 };
}

interface IndividualRowInfo {
  nombre: string;
  badges: string[];
  resumen: string;
}

function getIndividualInfo(grupo: GrupoIndividual, numeroProtocolo?: number): IndividualRowInfo {
  const fechaStr = formatDateAR(grupo.analisis.fecha_creacion);
  const numFmt =
    numeroProtocolo != null
      ? formatNumeroAnalisis(numeroProtocolo, grupo.analisis.numero_analisis)
      : null;

  function prefixed(nombre: string): string {
    return numFmt ? `${numFmt} — ${nombre}` : nombre;
  }

  switch (grupo.tipo) {
    case TipoAnalisis.COPROCULTIVO: {
      const c = grupo.analisis as Coprocultivo;
      const muestra = c.muestras_coprocultivo[0];
      let resumen = `Cargado el ${fechaStr}`;
      if (muestra) {
        const generos = [
          { nombre: 'Ostertagia', pct: muestra.porcentaje_ostertagia },
          { nombre: 'Trichostrongylus', pct: muestra.porcentaje_trichostrongylus },
          { nombre: 'Cooperia', pct: muestra.porcentaje_cooperia },
          { nombre: 'Haemonchus', pct: muestra.porcentaje_haemonchus },
          { nombre: 'Oesophagostomum', pct: muestra.porcentaje_oesophagostomum },
          { nombre: 'Nematodirus', pct: muestra.porcentaje_nematodirus },
        ]
          .filter((g) => g.pct > 0)
          .sort((a, b) => b.pct - a.pct)
          .slice(0, 2);
        if (generos.length > 0) {
          resumen = `${generos.map((g) => `${g.nombre} ${g.pct}%`).join(', ')} · Cargado el ${fechaStr}`;
        }
      }
      return { nombre: prefixed('Coprocultivo'), badges: [], resumen };
    }

    case TipoAnalisis.BAERMANN: {
      const b = grupo.analisis as Baermann;
      const positivo = b.pools.some((p) => p.valor === ResultadoBinario.POSITIVO);
      return {
        nombre: prefixed('Baermann'),
        badges: [],
        resumen: `${capitalize(positivo ? ResultadoBinario.POSITIVO : ResultadoBinario.NEGATIVO)} · Cargado el ${fechaStr}`,
      };
    }

    case TipoAnalisis.SEDIMENTACION_FH: {
      const s = grupo.analisis as SedimentacionFh;
      const positivo = s.pools.some((p) => p.valor === ResultadoBinario.POSITIVO);
      const nFotos = s.fotos.length;
      return {
        nombre: prefixed('Sedimentación Fh'),
        badges: nFotos > 0 ? [`${nFotos} foto${nFotos !== 1 ? 's' : ''}`] : [],
        resumen: `${capitalize(positivo ? ResultadoBinario.POSITIVO : ResultadoBinario.NEGATIVO)} · Cargado el ${fechaStr}`,
      };
    }

    case TipoAnalisis.ECTOPARASITOS: {
      const e = grupo.analisis as Ectoparasitos;
      const positivo = e.muestras_ectoparasitos.some((m) => m.valor === ResultadoBinario.POSITIVO);
      const nFotos = e.fotos.length;
      return {
        nombre: prefixed('Ectoparásitos'),
        badges: nFotos > 0 ? [`${nFotos} foto${nFotos !== 1 ? 's' : ''}`] : [],
        resumen: `${capitalize(positivo ? ResultadoBinario.POSITIVO : ResultadoBinario.NEGATIVO)} · Cargado el ${fechaStr}`,
      };
    }
  }
}

interface GrupoAnalisisRowProps {
  grupo: GrupoAnalisis;
  numeroProtocolo?: number;
  canManage: boolean;
  onEliminar: () => void;
  onEditar: (grupo: GrupoAnalisis) => void;
}

export function GrupoAnalisisRow({
  grupo,
  numeroProtocolo,
  canManage,
  onEliminar,
  onEditar,
}: GrupoAnalisisRowProps): JSX.Element {
  if (grupo.tipoGrupo === 'hpg') {
    const { titulo, subtitulo1, subtitulo2 } = getHpgInfo(grupo, numeroProtocolo);

    return (
      <Box
        onClick={() => onEditar(grupo)}
        sx={{
          bgcolor: 'background.paper',
          border: '0.5px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: '12px 14px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          cursor: 'pointer',
          '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
            {titulo}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitulo1}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitulo2}
          </Typography>
        </Box>

        {canManage && (
          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Tooltip title="Eliminar">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => { e.stopPropagation(); onEliminar(); }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Box>
    );
  }

  // Individual group
  const { nombre, badges, resumen } = getIndividualInfo(grupo, numeroProtocolo);

  return (
    <Box
      onClick={() => onEditar(grupo)}
      sx={{
        bgcolor: 'background.paper',
        border: '0.5px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: '12px 14px',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        cursor: 'pointer',
        '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5} flexWrap="wrap">
          <Typography variant="body2" fontWeight={600}>
            {nombre}
          </Typography>
          {badges.map((b) => (
            <Chip
              key={b}
              label={b}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: 11 }}
            />
          ))}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
          {resumen}
        </Typography>
      </Box>

      {canManage && (
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => { e.stopPropagation(); onEliminar(); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </Box>
  );
}
